import { FC } from 'react';
import { useRecoilValue } from 'recoil';
import { Controlled as CodeMirrorReact, DomEvent } from 'react-codemirror2';
import CodeMirror, { Editor, EditorChange, EditorConfiguration } from 'codemirror';

import { codeMirrorThemeState } from 'styles/stylesProvider';
import './CodeEditor.scss';

import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/theme/neo.css';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/addon/edit/matchbrackets';

/**
 * This is wrapper around the React CodeMirror component that sets up the Code mirror.
 * @props value                  current value of the code input
 * @props onChange               callback which is called whenever input value changing
 * @props className              className for CodeMirror component
 * @props options                any custom CodeMirror options
 * @props editorInstanceCallback a callback to return the CodeMirror instance
 */
type CodeEditorProps = {
  value: string;
  onChange: (editor: Editor, data: EditorChange, value: string) => void;
  onKeyUp?: DomEvent;
  className?: string;
  options?: EditorConfiguration;
  editorInstanceCallback?: (editor: Editor) => void;
  onSave?: () => void;
};

const CodeEditor: FC<CodeEditorProps> = ({
  value,
  onChange,
  onKeyUp,
  className = '',
  options,
  editorInstanceCallback,
  onSave,
}: CodeEditorProps) => {
  if (onSave) {
    // @ts-expect-error solution taken from here: https://github.com/scniro/react-codemirror2/issues/53
    CodeMirror.commands.save = onSave;
  }

  const codeMirrorTheme = useRecoilValue(codeMirrorThemeState);

  const defaultOptions = {
    mode: 'javascript',
    theme: codeMirrorTheme,
    lineNumbers: true,
    matchBrackets: true,
    autoFocus: true,
  };

  const onEditorRefresh = (editor: Editor): void => {
    editor.refresh();
    editor.setSize('100%', '100%');

    // Modal dialogs are instantiated long before being shown, so initial focus must be done
    // by the dialog itself
    if (editorInstanceCallback) {
      editorInstanceCallback(editor);
    }
  };

  return (
    <CodeMirrorReact
      className={`CodeEditor__CodeMirror ${className}`}
      value={value}
      options={{ ...defaultOptions, ...options }}
      editorDidMount={onEditorRefresh}
      onBeforeChange={onChange}
      {...(onKeyUp ? { onKeyUp } : {})}
    />
  );
};

export default CodeEditor;
